"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useEffect } from "react";
import { onCLS, onFCP, onFID, onINP, onLCP, onTTFB } from "web-vitals";
import { trackEvent } from "@/lib/analytics";

/**
 * Web Vitals component for monitoring and reporting Core Web Vitals metrics
 * @see https://web.dev/vitals/ for more information on Web Vitals
 */
function WebVitalsInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Only run in production
    if (process.env.NODE_ENV !== "production") {
      return;
    }

    // Report web vitals metrics when they're available
    const reportWebVital = ({
      name,
      delta,
      value,
      id,
    }: {
      name: string;
      delta: number;
      value: number;
      id: string;
    }) => {
      // Create the metric payload
      const metric = {
        name,
        value: Math.round(name === "CLS" ? delta * 1000 : delta),
        rating: getRating(name, value),
        delta: Math.round(delta),
        id,
        page: pathname,
        params: Object.fromEntries(searchParams.entries()),
      }; // Log to console in development
      if (process.env.NODE_ENV === "development") {
        console.log(`[Web Vitals]`, metric);
      }

      // Report to your analytics
      trackEvent({
        category: "web-vitals",
        action: metric.name,
        label: metric.id,
        value: Math.round(metric.value),
      });
    };

    // Monitor all the vitals
    onCLS(reportWebVital); // Cumulative Layout Shift
    onFID(reportWebVital); // First Input Delay
    onLCP(reportWebVital); // Largest Contentful Paint
    onFCP(reportWebVital); // First Contentful Paint
    onTTFB(reportWebVital); // Time to First Byte
    onINP(reportWebVital); // Interaction to Next Paint
  }, [pathname, searchParams]);
  return null;
}

// Export a wrapped version with Suspense
export function WebVitals() {
  return (
    <Suspense fallback={null}>
      <WebVitalsInner />
    </Suspense>
  );
}

// Helper to determine rating (good, needs-improvement, poor)
function getRating(
  metric: string,
  value: number,
): "good" | "needs-improvement" | "poor" {
  switch (metric) {
    case "CLS":
      return value <= 0.1
        ? "good"
        : value <= 0.25
          ? "needs-improvement"
          : "poor";
    case "FID":
      return value <= 100
        ? "good"
        : value <= 300
          ? "needs-improvement"
          : "poor";
    case "LCP":
      return value <= 2500
        ? "good"
        : value <= 4000
          ? "needs-improvement"
          : "poor";
    case "FCP":
      return value <= 1800
        ? "good"
        : value <= 3000
          ? "needs-improvement"
          : "poor";
    case "TTFB":
      return value <= 800
        ? "good"
        : value <= 1800
          ? "needs-improvement"
          : "poor";
    case "INP":
      return value <= 200
        ? "good"
        : value <= 500
          ? "needs-improvement"
          : "poor";
    default:
      return "needs-improvement";
  }
}
